import { Seo } from "../components/Seo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ChevronRight, 
  ArrowDown, 
  LineChart, 
  Building2, 
  Megaphone, 
  FileVideo, 
  Store, 
  ShieldCheck,
  Terminal,
  Plug,
  MessageSquare,
  Shield,
  Layers,
  ArrowRight,
  Target,
  Vault,
  Users,
  BadgeCheck,
  Wallet
} from "lucide-react";
import SwarmBackground from "../components/SwarmBackground";
import MetricsTicker from "../components/MetricsTicker";
import ArchitectureDiagram from "../components/ArchitectureDiagram";
import SwarmCrewSection from "../components/SwarmCrewSection";
import UseCaseCard from "../components/UseCaseCard";
import { GovernedLifecycle } from "../components/GovernedLifecycle";

// Phase 1 Wedge - Institutional use cases (neutral regulatory framing)
const phase1UseCases = [
  {
    title: "Treasury & Public Finance",
    description: "Institutional + sovereign treasury, government payments, custody — regulatory-grade governance.",
    icon: Vault,
    color: "violet",
    tam: "$50T+ AUM",
    href: "/use-cases#yield"
  },
  {
    title: "HR & Workforce Compliance",
    description: "Recruitment, promotion, termination — employment governance for high-risk AI systems.",
    icon: Users,
    color: "cyan",
    tam: "$93B"
  },
  {
    title: "Credit & Risk Decisioning",
    description: "Credit, insurance, claims — essential services compliance and audit trails.",
    icon: BadgeCheck,
    color: "green",
    tam: "$200B+"
  }
];

// Phase 2 Adjacencies
const phase2UseCases = [
  {
    title: "E-Commerce",
    description: "Repricing, inventory management, order automation with spending controls.",
    icon: Store,
    color: "slate",
    tam: "$6.3T"
  },
  {
    title: "Sports & Talent",
    description: "Player contracts, scouting agents, budget controls, transfer compliance.",
    icon: Target,
    color: "slate",
    tam: "$30B+",
    href: "/use-cases#sports"
  },
  {
    title: "Procurement & Sourcing",
    description: "Agent-driven RFPs, contract review, vendor compliance checks, spend governance.",
    icon: ShieldCheck,
    color: "slate",
    tam: "$93B"
  }
];

const connectionMethods = [
  {
    title: "API Gateway",
    description: "Enterprise integration with zero code changes. Deploy in minutes.",
    icon: Plug,
    label: "Zero Code"
  },
  {
    title: "SDK Integration",
    description: "Developer-friendly SDK. Five lines of code to full governance.",
    icon: Terminal,
    label: "5 Lines"
  },
  {
    title: "Assisted Onboarding",
    description: "AI-native setup. Chat with our agents to configure your swarm.",
    icon: MessageSquare,
    label: "AI Native"
  }
];

export const HomePage = () => {
  const scrollToContent = () => {
    document.getElementById('metrics-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="relative" data-testid="home-page">
      <Seo title="SwarmCitadel | Compliance OS for Regulated AI Agents" description="The compliance operating system for regulated AI agents. Runtime governance, tamper-evident audit trails, and labor attribution for autonomous AI systems." path="/" />
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <SwarmBackground />
        
        {/* Background mesh gradient */}
        <div className="absolute inset-0 bg-mesh pointer-events-none" />
        
        {/* Radial glow effects with parallax */}
        <div 
          className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-swarm-violet/15 rounded-full blur-[150px] animate-pulse" 
          data-parallax="0.03"
        />
        <div 
          className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-swarm-cyan/10 rounded-full blur-[150px] animate-pulse" 
          style={{ animationDelay: '1s' }}
          data-parallax="0.02"
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 text-center">
          {/* Logomark only (icon with swarm effect) */}
          <div className="flex flex-col items-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/xudosnj3_swarmcitadel-logomark-primary-white-transparent-1254x1254.png"
              alt="SwarmCitadel"
              className="w-24 h-24 md:w-28 md:h-28 object-contain mb-6"
            />
            
            {/* Tagline Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-swarm-border animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-swarm-green animate-pulse" />
              <span className="text-slate-400 text-sm font-medium">
                Pre-Seed · 5 US Patents Filed
              </span>
            </div>
          </div>
          {/* Main Headline */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-white mb-4 animate-slide-in-up">
            The Compliance Operating System
          </h1>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-10 animate-slide-in-up">
            <span className="text-gradient-safe">for Regulated AI Agents</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg md:text-xl text-slate-400 max-w-3xl mx-auto mb-10 leading-relaxed animate-slide-in-up animate-delay-200">
            Governance Engine. Tamper-Evident Audit. Labor Attribution Ledger.
            <br className="hidden md:block" />
            The substrate regulators require for autonomous AI agents.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-slide-in-up animate-delay-300">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="btn-glow h-14 px-8 text-base"
                data-testid="hero-cta-demo"
                data-magnetic
              >
                Request Demo
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/docs">
              <Button 
                variant="outline" 
                size="lg" 
                className="btn-outline h-14 px-8 text-base"
                data-testid="hero-cta-docs"
                data-magnetic
              >
                Read the Docs
              </Button>
            </Link>
          </div>
          
          {/* Scroll Indicator */}
          <button 
            onClick={scrollToContent}
            className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-500 hover:text-slate-300 transition-colors animate-float"
            data-testid="scroll-indicator"
          >
            <span className="text-xs uppercase tracking-widest">Explore</span>
            <ArrowDown className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* Metrics Ticker */}
      <div id="metrics-section">
        <MetricsTicker />
      </div>

      {/* Three Pillars Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">The Platform</div>
            <h2 className="section-title">Three Pillars of Agent Infrastructure</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Governance Rails */}
            <div className="glass-card p-8 border-swarm-violet/20 hover:border-swarm-violet/40 transition-all">
              <div className="w-14 h-14 rounded-2xl bg-swarm-violet/10 flex items-center justify-center mb-6">
                <Shield className="w-7 h-7 text-swarm-violet" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Governance Rails</h3>
              <p className="text-slate-400 leading-relaxed">
                Policy engine evaluates every agent action before execution. Spending limits, 
                approval chains, audit trails, and automatic revenue splits. The CFO, COO, CISO, 
                and compliance team for your agents.
              </p>
            </div>

            {/* Connector Ecosystem */}
            <div className="glass-card p-8 border-swarm-cyan/20 hover:border-swarm-cyan/40 transition-all">
              <div className="w-14 h-14 rounded-2xl bg-swarm-cyan/10 flex items-center justify-center mb-6">
                <Plug className="w-7 h-7 text-swarm-cyan" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Connector Ecosystem</h3>
              <p className="text-slate-400 leading-relaxed">
                39+ platform integrations today including Shopify, Google Ads, SAP, Uniswap, 
                and Polymarket. Financial connectors coming soon: virtual cards for agents, 
                bank accounts, and payment rails.
              </p>
            </div>

            {/* Agent Marketplace */}
            <div className="glass-card p-8 border-swarm-green/20 hover:border-swarm-green/40 transition-all">
              <div className="w-14 h-14 rounded-2xl bg-swarm-green/10 flex items-center justify-center mb-6">
                <Store className="w-7 h-7 text-swarm-green" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Agent Marketplace</h3>
              <p className="text-slate-400 leading-relaxed">
                AI agent providers publish their agents. Your company installs and runs them. 
                Every agent governed by your policy engine. Browse, install, set policies, go.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three-Layer Architecture Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Platform Architecture</div>
            <h2 className="section-title">Three Layers of Control</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              A complete stack for institutional-grade agent operations.
              Click each layer to explore capabilities.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <ArchitectureDiagram />
          </div>
        </div>
      </section>

      {/* x402 Agent Payments Section */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-card p-8 md:p-12 border-swarm-cyan/30 relative overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-swarm-cyan/10 rounded-full blur-[100px]" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-500 text-xs font-medium mb-4">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  In development
                </div>
                <div className="section-label text-swarm-cyan">x402 Agent Payments</div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Agents can now pay for anything.
                  <br />
                  <span className="text-swarm-cyan">We decide whether they should.</span>
                </h3>
                <p className="text-slate-400 max-w-xl">
                  The open x402 standard lets AI agents pay APIs over HTTP in stablecoins. SwarmCitadel adds the policy engine, budgets and audit trail that turn an ungoverned wallet into a corporate account.
                </p>
              </div>
              <Link to="/x402" className="flex-shrink-0">
                <Button 
                  size="lg" 
                  className="bg-swarm-cyan hover:bg-swarm-cyan/90 text-white"
                  data-testid="x402-homepage-cta"
                >
                  See how it works
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SwarmCrew Section */}
      <section className="py-24 md:py-32 bg-swarm-darker/50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <SwarmCrewSection />
        </div>
      </section>

      {/* OpenClaw Governance Callout */}
      <section className="py-16 md:py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="glass-card p-8 md:p-12 border-swarm-green/30 relative overflow-hidden">
            {/* Background glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-swarm-green/10 rounded-full blur-[100px]" />
            
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
              <div className="flex-1">
                <div className="section-label text-swarm-green">OpenClaw Governance</div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  300K+ GitHub Stars. Zero Governance.
                  <br />
                  <span className="text-swarm-green">We fix that.</span>
                </h3>
                <p className="text-slate-400 max-w-xl">
                  300K+ users, 512 known vulnerabilities, zero spending controls.
                  SwarmCitadel provides the institutional governance layer for any ungoverned agent framework.
                </p>
              </div>
              <Link to="/use-cases" className="flex-shrink-0">
                <Button 
                  size="lg" 
                  className="bg-swarm-green hover:bg-swarm-green/90 text-white"
                  data-testid="openclaw-cta"
                >
                  Explore Use Cases
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond Finance: Three New Frontiers */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">New Frontiers</div>
            <h2 className="section-title">Beyond Finance: Three New Frontiers</h2>
            <p className="text-slate-400 max-w-3xl mx-auto text-lg">
              The same governance engine that controls DeFi agents can govern any domain. 
              In the Middle East, governance is the #1 requirement for sovereign funds, 
              sports organizations, and national AI programs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Sports Management */}
            <div className="glass-card p-8 border-[#2DD4A8]/30 hover:border-[#2DD4A8]/50 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-[#2DD4A8]/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-7 h-7 text-[#2DD4A8]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Sports Management</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A8] mt-1.5 flex-shrink-0" />
                  Player contract KPI monitoring and automated payouts
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A8] mt-1.5 flex-shrink-0" />
                  Budget controls per department (scouting, operations, academy)
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A8] mt-1.5 flex-shrink-0" />
                  Scouting agent marketplace: teams monetize proprietary AI scouts
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A8] mt-1.5 flex-shrink-0" />
                  Transfer compliance, wage cap enforcement, audit trails
                </li>
              </ul>
              <div className="pt-4 border-t border-swarm-border">
                <span className="text-[#2DD4A8] text-sm font-mono">$30B+ global sports market</span>
              </div>
            </div>

            {/* Government & Sovereign Funds */}
            <div className="glass-card p-8 border-amber-500/30 hover:border-amber-500/50 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Building2 className="w-7 h-7 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Government & Sovereign Funds</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  Board→Committee→CEO delegation of authority mirroring real org structures
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  AI agent governance for sovereign wealth portfolio companies
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  GRC compliance (Shariah, local content, budget ceilings, internal audit)
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" />
                  Data sovereignty and national AI program controls
                </li>
              </ul>
              <div className="pt-4 border-t border-swarm-border">
                <span className="text-amber-500 text-sm font-mono">$3T+ sovereign wealth AUM</span>
              </div>
            </div>

            {/* Smart Contract as a Service */}
            <div className="glass-card p-8 border-swarm-violet/30 hover:border-swarm-violet/50 transition-all group">
              <div className="w-14 h-14 rounded-2xl bg-swarm-violet/10 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <Shield className="w-7 h-7 text-swarm-violet" />
              </div>
              <h3 className="text-xl font-bold text-white mb-4">Smart Contract as a Service</h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-swarm-violet mt-1.5 flex-shrink-0" />
                  No-code smart contract creation tied to agent actions
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-swarm-violet mt-1.5 flex-shrink-0" />
                  Immutable ledger for governance decisions on-chain
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-swarm-violet mt-1.5 flex-shrink-0" />
                  Escrow, milestone payments, and conditional execution
                </li>
                <li className="flex items-start gap-2 text-slate-400 text-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-swarm-violet mt-1.5 flex-shrink-0" />
                  Blockchain-optional: use when transparency is required
                </li>
              </ul>
              <div className="pt-4 border-t border-swarm-border">
                <span className="text-swarm-violet text-sm font-mono">Technical moat / platform lock-in</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Governed Action Lifecycle */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="lifecycle-section">
        <div className="absolute inset-0 bg-mesh opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] bg-swarm-violet/10 rounded-full blur-[160px]" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="max-w-3xl mb-14">
            <div className="section-label">The Governed Action Lifecycle</div>
            <h2 className="section-title">
              Every action evaluated.
              <br />
              <span className="text-gradient inline-block">Every outcome verifiable.</span>
            </h2>
            <p className="text-slate-400 text-lg mt-4">
              Watch a live stream of agent proposals pass through the policy engine. Approved actions execute within bound limits,
              risky ones escalate to a human, violations are blocked — and every single step lands in a hash-linked record a regulator can verify offline.
            </p>
          </div>
          <GovernedLifecycle />
        </div>
      </section>

      {/* Platform Ecosystem Section */}
      <section className="py-24 md:py-32 bg-swarm-darker/50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Ecosystem</div>
            <h2 className="section-title">Not Just Rails. An Ecosystem.</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              AI agent providers want distribution. Companies want plug-and-play agents. 
              SwarmCitadel connects them under governance.
            </p>
          </div>

          {/* Three Layer Visual */}
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Agent Providers Layer */}
            <div className="glass-card p-6 border-swarm-violet/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-swarm-violet/10">
                  <Layers className="w-5 h-5 text-swarm-violet" />
                </div>
                <h3 className="text-lg font-semibold text-white">Agent Providers</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {["DeFi Trading Bots", "SEM Optimizers", "Support Agents", "Procurement", "Inventory Managers", "Content Creators", "Compliance Monitors", "Fraud Detectors"].map((agent) => (
                  <span key={agent} className="px-3 py-1 bg-swarm-violet/10 text-swarm-violet text-xs rounded-full">
                    {agent}
                  </span>
                ))}
              </div>
              <p className="text-slate-500 text-sm italic">"Publish once. Reach every operator."</p>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-slate-600" />
            </div>

            {/* SwarmCitadel Platform Layer */}
            <div className="glass-card p-6 border-swarm-cyan/50 bg-swarm-cyan/5 shadow-[0_0_40px_rgba(6,182,212,0.1)]">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_sentinel-hub-28/artifacts/jsghs2ap_swarmcitadel-horizontal-primary-dark-bg-2172x724.png"
                  alt="SwarmCitadel"
                  className="h-8 object-contain"
                />
              </div>
              <div className="flex flex-wrap gap-3">
                {["Governance Engine", "Connector Marketplace", "Agent Marketplace", "Financial Rails", "Audit Trail"].map((feature) => (
                  <span key={feature} className="px-4 py-2 bg-swarm-cyan/10 text-swarm-cyan text-sm font-medium rounded-lg border border-swarm-cyan/20">
                    {feature}
                  </span>
                ))}
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-slate-600" />
            </div>

            {/* Operators Layer */}
            <div className="glass-card p-6 border-swarm-green/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-swarm-green/10">
                  <Building2 className="w-5 h-5 text-swarm-green" />
                </div>
                <h3 className="text-lg font-semibold text-white">Operators / Companies</h3>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                {["Trading Desks", "E-commerce Brands", "Marketing Agencies", "DAOs", "Real Estate Firms", "Enterprise Procurement", "Affiliate Networks"].map((operator) => (
                  <span key={operator} className="px-3 py-1 bg-swarm-green/10 text-swarm-green text-xs rounded-full">
                    {operator}
                  </span>
                ))}
              </div>
              <p className="text-slate-500 text-sm italic">"Install agents. Set policies. Go."</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Use Cases</div>
            <h2 className="section-title">Compliance OS for Regulated Industries</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Built for binding AI governance frameworks and high-risk system requirements.
            </p>
          </div>
          
          {/* Phase 1 Wedge - Institutional */}
          <div className="mb-8">
            <h3 className="text-sm font-mono text-swarm-violet uppercase tracking-wider mb-6">Phase 1 Wedge — Institutional</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {phase1UseCases.map((useCase, index) => (
                <UseCaseCard key={useCase.title} {...useCase} index={index} />
              ))}
            </div>
          </div>

          {/* Phase 2 Adjacencies */}
          <div className="mt-12">
            <h3 className="text-sm font-mono text-slate-500 uppercase tracking-wider mb-6">Phase 2 Adjacencies</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 opacity-70">
              {phase2UseCases.map((useCase, index) => (
                <UseCaseCard key={useCase.title} {...useCase} index={index + 3} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How Agents Connect Section */}
      <section className="py-24 md:py-32 bg-swarm-darker/50 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="section-label">Integration</div>
            <h2 className="section-title">How Agents Connect</h2>
            <p className="text-slate-400 max-w-2xl mx-auto text-lg">
              Three paths to governance. Choose yours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {connectionMethods.map((method, index) => {
              const Icon = method.icon;
              return (
                <div 
                  key={method.title}
                  className="glass-card-hover p-8 text-center animate-slide-in-up"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="w-16 h-16 rounded-2xl bg-swarm-violet/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-8 h-8 text-swarm-violet" />
                  </div>
                  <div className="inline-block px-3 py-1 rounded-full bg-swarm-cyan/10 text-swarm-cyan text-xs font-mono mb-4">
                    {method.label}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {method.title}
                  </h3>
                  <p className="text-slate-400 text-sm">
                    {method.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-mesh" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-swarm-violet/10 rounded-full blur-[200px]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6">
            Ready to govern your agents?
          </h2>
          <p className="text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Join the private beta and be among the first to experience institutional-grade agent governance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact">
              <Button 
                size="lg" 
                className="btn-glow h-14 px-10 text-base"
                data-testid="footer-cta-demo"
              >
                Request Demo
                <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </Link>
            <Link to="/investors">
              <Button 
                variant="outline" 
                size="lg" 
                className="btn-outline h-14 px-10 text-base"
                data-testid="footer-cta-investors"
              >
                Investor Access
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
